import { FC, useState } from "react";

import { sanitizeAndTransformHtml } from "@moodys/mdc-frontend.utils.html-sanitizer";
import { Alert, AlertTitle, Button, Typography } from "@mui/material";

import { MoodysThemeProviderWhitney } from "components/MoodysThemeProviderWhitney";

import { generateAlertDismissalStorageKey } from "../../alertBannerWrapper.helpers";

import { styles } from "./AlertBanner.styles";
import { getAlertSeverity } from "./alertBanner.helpers";
import { AlertBannerContentWithRequiredFields } from "./alertBanner.types";
import { ALERT_TEXT_PARSER_OPTIONS } from "./parserOptions";

export interface AlertBannerProps {
  content: AlertBannerContentWithRequiredFields;
}

export const AlertBanner: FC<AlertBannerProps> = ({ content }) => {
  const ariaRole = content.urgent ? "alert" : "status";
  const title = content.title;
  const [showBanner, setShowBanner] = useState(true);

  const handleAlertDismissal = (alertId: string) => {
    localStorage.setItem(generateAlertDismissalStorageKey(alertId), "true");
    setShowBanner(false);
    window.scrollBy({
      top: -1,
      behavior: "smooth",
    });
  };

  if (!showBanner) {
    return null;
  }

  return (
    <MoodysThemeProviderWhitney colorPalette="default">
      <Alert
        css={styles.alert}
        role={ariaRole}
        severity={getAlertSeverity(content.severity)}
        square
        data-testid="global-alert-banner"
        action={
          <Button
            color="inherit"
            variant="outlined"
            size="small"
            onClick={() => handleAlertDismissal(content.id)}
            data-testid={`dismiss-alert-${content.id}-button`}
          >
            Dismiss
          </Button>
        }
      >
        {title && (
          <AlertTitle
            css={styles.title}
            data-testid="global-alert-banner-title"
          >
            {sanitizeAndTransformHtml({
              htmlString: title,
              parserOptions: ALERT_TEXT_PARSER_OPTIONS,
            })}
          </AlertTitle>
        )}
        <Typography
          variant="body2"
          data-test-id="global-alert-banner-message"
          component="div"
        >
          {sanitizeAndTransformHtml({
            htmlString: content.message,
            parserOptions: ALERT_TEXT_PARSER_OPTIONS,
          })}
        </Typography>
      </Alert>
    </MoodysThemeProviderWhitney>
  );
};
